import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { TrendingUp, TrendingDown, Receipt, RotateCcw, Download, FileText, FileSpreadsheet, ChevronRight, ChevronDown } from "lucide-react";
import { exportCSV, exportExcel, exportPDF } from "../components/TableExport";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

const raisonTypes = [
  { value: "demi_journee", label: "Demi-journee", color: "bg-amber-100 text-amber-800 border-amber-300" },
  { value: "repos", label: "Jour de repos", color: "bg-slate-100 text-slate-700 border-slate-300" },
  { value: "dette", label: "Dette (paiera apres)", color: "bg-red-100 text-red-800 border-red-300" },
  { value: "reparation", label: "Reparation deduite", color: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "amende", label: "Amende a regler", color: "bg-orange-100 text-orange-800 border-orange-300" },
  { value: "remboursement_dette", label: "Remboursement dette", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { value: "remboursement_amende", label: "Remboursement amende", color: "bg-teal-100 text-teal-800 border-teal-300" },
  { value: "autre", label: "Autre", color: "bg-slate-100 text-slate-600 border-slate-200" },
];
const getRaisonColor = (type) => raisonTypes.find(r => r.value === type)?.color || "bg-slate-100 text-slate-600";
const getRaisonLabel = (type) => raisonTypes.find(r => r.value === type)?.label || type;

export default function InvestorRecettesPage() {
  const [recettes, setRecettes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [vehiculeId, setVehiculeId] = useState("all");
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = "/investor/recettes?";
      if (dateDebut) url += `date_debut=${dateDebut}&`;
      if (dateFin) url += `date_fin=${dateFin}&`;
      if (vehiculeId && vehiculeId !== "all") url += `vehicule_id=${vehiculeId}&`;
      const [rRes, vRes] = await Promise.all([api.get(url), api.get("/vehicles")]);
      setRecettes(rRes.data);
      setVehicles(vRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [dateDebut, dateFin, vehiculeId]);

  const resetFilters = () => {
    setDateDebut("");
    setDateFin("");
    setVehiculeId("all");
  };

  const totalPaye = recettes.reduce((s, r) => s + (r.montant_paye || 0), 0);
  const totalAttendu = recettes.reduce((s, r) => s + (r.montant_attendu || 0), 0);
  const totalDiff = totalPaye - totalAttendu;

  const recettesExportCols = [
    { label: "Date", key: "date_location" },
    { label: "Vehicule", accessor: (r) => `${r.vehicule_nom} (${r.vehicule_immat})` },
    { label: "Chauffeur", accessor: (r) => r.chauffeur_nom || "-" },
    { label: "Attendu", key: "montant_attendu" },
    { label: "Paye", key: "montant_paye" },
    { label: "Ecart", accessor: (r) => (r.montant_paye || 0) - (r.montant_attendu || 0) },
    { label: "Statut", key: "statut" },
  ];

  return (
    <div data-testid="investor-recettes-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Mes Recettes</h1>
          <p className="text-sm text-slate-500 mt-1">{recettes.length} recette{recettes.length > 1 ? "s" : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Select value={vehiculeId} onValueChange={setVehiculeId}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="filter-vehicle">
              <SelectValue placeholder="Tous les vehicules" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les vehicules</SelectItem>
              {vehicles.map(v => (
                <SelectItem key={v.id} value={v.id}>{v.marque} {v.modele} - {v.immatriculation}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Du</Label>
            <Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="w-[140px] text-sm" data-testid="filter-date-start" />
          </div>
          <div className="flex items-center gap-1">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Au</Label>
            <Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="w-[140px] text-sm" data-testid="filter-date-end" />
          </div>
          <Button variant="outline" size="sm" onClick={resetFilters} data-testid="reset-filters-btn">
            <RotateCcw className="w-4 h-4 mr-1" />Reinitialiser
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="btn-export-inv-recettes">
                <Download className="w-4 h-4 mr-2" />Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportCSV(recettes, recettesExportCols, "recettes-investisseur")} data-testid="export-inv-recettes-csv"><FileText className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportExcel(recettes, recettesExportCols, "recettes-investisseur")} data-testid="export-inv-recettes-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF(recettes, recettesExportCols, "recettes-investisseur", "Mes Recettes")} data-testid="export-inv-recettes-pdf"><FileText className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-testid="recettes-summary">
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
          </div>
          <p className="text-2xl font-bold text-slate-900 font-heading">{totalPaye} Frc</p>
          <p className="text-xs text-slate-500 mt-1">Total paye</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Receipt className="w-5 h-5 text-[#0F52BA]" /></div>
          </div>
          <p className="text-2xl font-bold text-slate-900 font-heading">{totalAttendu} Frc</p>
          <p className="text-xs text-slate-500 mt-1">Total attendu</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><TrendingDown className="w-5 h-5 text-amber-600" /></div>
          </div>
          <p className={`text-2xl font-bold font-heading ${totalDiff >= 0 ? "text-emerald-600" : "text-red-600"}`}>{totalDiff} Frc</p>
          <p className="text-xs text-slate-500 mt-1">Ecart</p>
        </div>
      </div>

      {/* Recettes Table */}
      {loading ? (
        <div className="text-slate-500 text-center py-12">Chargement...</div>
      ) : (
        <div className="table-container">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead className="font-semibold text-slate-600">Date</TableHead>
                <TableHead className="font-semibold text-slate-600">Vehicule</TableHead>
                <TableHead className="font-semibold text-slate-600">Chauffeur</TableHead>
                <TableHead className="font-semibold text-slate-600">Attendu</TableHead>
                <TableHead className="font-semibold text-slate-600">Paye</TableHead>
                <TableHead className="font-semibold text-slate-600">Ecart</TableHead>
                <TableHead className="font-semibold text-slate-600">Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recettes.map((r, i) => {
                const diff = (r.montant_paye || 0) - (r.montant_attendu || 0);
                const hasRaisons = (r.raisons_difference || []).length > 0;
                const isExpanded = expandedRows[r.id || i];
                return (
                  <>
                    <TableRow key={r.id || i} data-testid={`recette-row-${i}`} className={hasRaisons ? "cursor-pointer hover:bg-slate-50/80" : ""} onClick={() => hasRaisons && toggleRow(r.id || i)}>
                      <TableCell className="text-slate-600">
                        <div className="flex items-center gap-1.5">
                          {hasRaisons && (isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />)}
                          {r.date_location}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        {r.vehicule_nom}<br />
                        <span className="text-xs text-slate-500">{r.vehicule_immat}</span>
                      </TableCell>
                      <TableCell className="text-slate-600">{r.chauffeur_nom || "-"}</TableCell>
                      <TableCell className="text-slate-600">{r.montant_attendu} Frc</TableCell>
                      <TableCell className="text-emerald-600 font-medium">{r.montant_paye} Frc</TableCell>
                      <TableCell className={`font-medium ${diff >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {diff} Frc
                        {hasRaisons && !isExpanded && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(r.raisons_difference || []).slice(0, 2).map((ra, idx) => (
                              <Badge key={idx} variant="outline" className={`text-[10px] px-1.5 py-0 ${getRaisonColor(ra.type)}`}>
                                {getRaisonLabel(ra.type)}
                              </Badge>
                            ))}
                            {(r.raisons_difference || []).length > 2 && <span className="text-[10px] text-slate-400">+{(r.raisons_difference || []).length - 2}</span>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          r.statut === "paye" ? "border-emerald-200 text-emerald-700 bg-emerald-50" :
                          r.statut === "partiel" ? "border-amber-200 text-amber-700 bg-amber-50" :
                          "border-red-200 text-red-700 bg-red-50"
                        }>{r.statut}</Badge>
                      </TableCell>
                    </TableRow>
                    {hasRaisons && isExpanded && (
                      <TableRow key={`${r.id || i}-detail`} data-testid={`recette-detail-${i}`}>
                        <TableCell colSpan={7} className="bg-slate-50/70 py-3 px-6">
                          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Raisons de l'ecart</div>
                          <div className="space-y-1.5">
                            {(r.raisons_difference || []).map((ra, idx) => (
                              <div key={idx} className="flex items-center gap-3 text-sm">
                                <Badge variant="outline" className={`text-xs ${getRaisonColor(ra.type)}`}>{getRaisonLabel(ra.type)}</Badge>
                                {ra.description && <span className="text-slate-600">{ra.description}</span>}
                                {ra.montant !== undefined && ra.montant !== 0 && <span className="font-medium text-slate-900 ml-auto">{ra.montant} Frc</span>}
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
              {recettes.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Aucune recette trouvee</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
